import { useId, useState } from 'react';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { useTravelText } from '../../app/TravelLocaleContext';
import { formatDateTimeValue } from '../../i18n/travelFormat';
import { publicTimelineTypeLabelKey } from '../public-journey/publicJourneyPresentation';
import {
  isMemoryImageObjectKey,
  MEDIA_UPLOAD_STATUS,
  MEMORY_MEDIA_KIND,
  memoryMediaSlotKind,
} from './journeyMemoryModel';

const P = 'travel.publication.story.preview';

/**
 * 預覽用的圖片來源白名單：
 * 非 IMAGE（明確 VIDEO、私有影片 key、未知媒體）不渲染；本機預覽只接受頁面建立的 blob: URL；
 * 已上傳或既有媒體只在 objectKey 符合後端圖片規則時沿用既有讀取方式。
 */
export function storyPreviewImageSrc(slot) {
  if (!slot || memoryMediaSlotKind(slot) !== MEMORY_MEDIA_KIND.IMAGE) return '';
  const previewUrl = String(slot.previewUrl || '');
  if (previewUrl.startsWith('blob:')) return previewUrl;
  const objectKey = String(slot.objectKey || '').trim();
  return isMemoryImageObjectKey(objectKey) ? resolveMemberMediaUrl(objectKey) : '';
}

/** 依可見性與類型挑選觀看範圍說明；不推導選入、授權或發布的實際狀態。 */
function audienceKey(draft) {
  if (draft?.memoryType === 'PRIVATE_NOTE') return `${P}.audience.privateNote`;
  if (draft?.visibility === 'PUBLIC') return `${P}.audience.public`;
  if (draft?.visibility === 'FOLLOWERS') return `${P}.audience.followers`;
  return `${P}.audience.private`;
}

/**
 * 日誌表單的「預覽故事樣式」：以公開故事卡片樣式呈現目前草稿。
 * 純前端呈現，不呼叫資料 API、不建立 Blob、不改變表單或媒體狀態。
 */
export default function MemoryStoryPreview({ draft, mediaSlots }) {
  const regionId = useId();
  const headingId = useId();
  const [open, setOpen] = useState(false);
  const { t, tf, intlLocale } = useTravelText();
  // 上傳失敗的圖片不會被儲存，預覽也不顯示
  const media = (mediaSlots || []).filter((slot) => slot.status !== MEDIA_UPLOAD_STATUS.FAILED);
  const title = String(draft?.title || '').trim();
  const content = String(draft?.content || '');
  const occurredAt = formatDateTimeValue(draft?.occurredAt, intlLocale);

  return (
    <div className="journey-journal__story-preview" data-testid="memory-story-preview">
      <button
        type="button"
        className="journey-journal__story-preview-toggle"
        aria-expanded={open}
        aria-controls={regionId}
        data-testid="memory-story-preview-toggle"
        onClick={() => setOpen((value) => !value)}
      >
        {t(open ? `${P}.hide` : `${P}.show`)}
      </button>
      <section
        id={regionId}
        className="journey-journal__story-preview-region"
        aria-labelledby={headingId}
        hidden={!open}
      >
        {open ? (
          <>
            <h4 id={headingId}>{t(`${P}.heading`)}</h4>
            <p className="journey-journal__story-preview-notice">{t(`${P}.draftNotice`)}</p>
            <article className="journey-journal__story-preview-card" data-testid="memory-story-preview-card">
              <p className="journey-journal__story-preview-meta">
                <span>{t(publicTimelineTypeLabelKey({ type: 'MEMORY', memoryType: draft?.memoryType }))}</span>
              </p>
              {title ? <h5 data-testid="memory-story-preview-title">{title}</h5> : null}
              {content.trim() ? (
                <p className="journey-journal__story-preview-content" data-testid="memory-story-preview-content">{content}</p>
              ) : (
                <p className="journey-journal__story-preview-empty">{t(`${P}.emptyContent`)}</p>
              )}
              {media.length > 0 ? (
                <ol className="journey-journal__story-preview-media" data-testid="memory-story-preview-media">
                  {media.map((slot, index) => {
                    const src = storyPreviewImageSrc(slot);
                    return (
                      <li key={slot.localId || `${slot.objectKey}-${index}`} data-testid="memory-story-preview-media-item">
                        {src ? (
                          <img
                            src={src}
                            alt={tf(`${P}.imageAlt`, { index: index + 1, total: media.length })}
                            loading="lazy"
                          />
                        ) : (
                          <span className="journey-journal__story-preview-unavailable">
                            {t(memoryMediaSlotKind(slot) === MEMORY_MEDIA_KIND.VIDEO
                              ? 'travel.journal.media.videoUnsupported'
                              : `${P}.mediaUnavailable`)}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              ) : null}
              {occurredAt ? <time dateTime={draft?.occurredAt}>{occurredAt}</time> : null}
            </article>
            <p className="journey-journal__story-preview-audience" data-testid="memory-story-preview-audience">
              {t(audienceKey(draft))}
            </p>
            <h5>{t(`${P}.stepsTitle`)}</h5>
            <ol className="journey-journal__story-preview-steps" data-testid="memory-story-preview-steps">
              <li>{t(`${P}.step.visibility`)}</li>
              <li>{t(`${P}.step.selection`)}</li>
              <li>{t(`${P}.step.consent`)}</li>
              <li>{t(`${P}.step.publish`)}</li>
            </ol>
          </>
        ) : null}
      </section>
    </div>
  );
}
